import client from 'helpers/client';
import cognito from 'helpers/cognito';
import axios from 'axios';
import config from 'config';

const PAYMENT_REQUEST_PATH = '/pay';

const getAuthHeaders = async () => ({
  headers: await cognito.getHeaderAuthorization(),
});

export const normalizeMemoHash = (memoHash) => {
  if (!memoHash) return '';
  const normalized = String(memoHash).trim().toLowerCase();
  return normalized.startsWith('0x') ? normalized : `0x${normalized}`;
};

/** Public payment page URL using the current app host (window.location.origin). */
export const buildPaymentRequestUrl = (memoHash) => {
  const normalized = normalizeMemoHash(memoHash);
  if (!normalized || typeof window === 'undefined') return '';
  return `${window.location.origin}${PAYMENT_REQUEST_PATH}?memoHash=${normalized}`;
};

export const getPaymentRequestByMemoHash = async (memoHash) => {
  try {
    const response = await client.api(`/payment-requests/by-memo/${memoHash}`, 'get', await getAuthHeaders());
    return response.data;
  } catch (error) {
    console.error('Error getting payment request by memo hash:', error);
    throw error;
  }
};

export const getPaymentRequests = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const query = params.toString();
    const response = await client.api(`/payment-requests${query ? `?${query}` : ''}`, 'get', await getAuthHeaders());

    return response.data;
  } catch (error) {
    console.error('Error getting payment requests:', error);
    throw error;
  }
};

export const getPublicRevenueItems = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });
    const query = params.toString();
    const response = await client.api(`/revenue-items/transparency${query ? `?${query}` : ''}`, 'get');
    return response.data;
  } catch (error) {
    console.error('Error getting public revenue items:', error);
    throw error;
  }
};

/** Fetch every public revenue item (paginates at API max 100 per request). */
export const fetchAllPublicRevenueItems = async (filters = {}) => {
  const pageSize = 100;
  let page = 1;
  let totalPages = 1;
  const all = [];

  do {
    const res = await getPublicRevenueItems({ ...filters, page, limit: pageSize });
    const chunk = res?.data || [];
    all.push(...chunk);
    totalPages = res?.pagination?.totalPages || 1;
    page += 1;
  } while (page <= totalPages);

  return all;
};

export const getRevenueItems = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    const query = params.toString();
    const response = await client.api(`/revenue-items${query ? `?${query}` : ''}`, 'get', await getAuthHeaders());

    return response.data;
  } catch (error) {
    console.error('Error getting revenue items:', error);
    throw error;
  }
};

export const createPaymentRequest = async (body) => {
  try {
    const response = await client.api('/payment-requests', 'post', body, await getAuthHeaders());
    return response.data;
  } catch (error) {
    console.error('Error creating payment request:', error);
    throw error;
  }
};

export const updateDaoVaultAddress = async (daoId, vaultAddress) => {
  try {
    const response = await client.api(
      `/daos/${daoId}`,
      'put',
      { vaultAddress },
      await getAuthHeaders(),
    );
    return response.data;
  } catch (error) {
    console.error('Error updating DAO vault address:', error);
    throw error;
  }
};

export const updateDaoMicrojobConfig = async (daoId, body, walletAddress) => {
  try {
    const headers = await getAuthHeaders();
    const response = await client.api(
      `/daos/${daoId}/microjob/config`,
      'put',
      { ...(body || {}), walletAddress: walletAddress ? String(walletAddress).trim() : undefined },
      headers,
    );
    return response.data;
  } catch (error) {
    console.error('Error updating DAO microjob config:', error);
    throw error;
  }
};

/**
 * Submit on-chain payment proof after a successful blockchain transaction.
 * Requires Cognito authentication matching recipientEmail.
 */
export const submitOnchainPayment = async ({ memoHash, txHash, chainId, buyerName }) => {
  try {
    const response = await client.api(
      '/payment-requests/submit-onchain-payment',
      'post',
      {
        memoHash,
        txHash,
        chainId,
        buyerName,
      },
      await getAuthHeaders(),
    );
    return response.data;
  } catch (error) {
    console.error('Error submitting on-chain payment proof:', error);
    throw error;
  }
};

export const renewPaymentRequest = async (memoHash, { expiresAt } = {}) => {
  try {
    const normalized = normalizeMemoHash(memoHash);
    const response = await client.api(
      `/payment-requests/by-memo/${encodeURIComponent(normalized)}/renew`,
      'post',
      { expiresAt },
      await getAuthHeaders(),
    );
    return response.data;
  } catch (error) {
    console.error('Error renewing payment request:', error);
    throw error;
  }
};

export const downloadPaymentReceipt = async (memoHash) => {
  const normalized = normalizeMemoHash(memoHash);
  if (!normalized) {
    throw new Error('Invalid memo hash');
  }

  const authHeaders = await cognito.getHeaderAuthorization();

  const response = await axios.get(
    `${config.api.api}/payment-requests/by-memo/${encodeURIComponent(normalized)}/receipt`,
    {
      responseType: 'blob',
      params: { _t: Date.now() },
      headers: authHeaders,
    },
  );

  return response.data;
};

export default {
  normalizeMemoHash,
  buildPaymentRequestUrl,
  getPaymentRequestByMemoHash,
  getPaymentRequests,
  getRevenueItems,
  createPaymentRequest,
  updateDaoVaultAddress,
  updateDaoMicrojobConfig,
  submitOnchainPayment,
  renewPaymentRequest,
  downloadPaymentReceipt,
};
